// src/components/ModalPremioDetalhe.js
// Detalhe do prêmio. Mantém as mesmas props de antes
// (premio, aberto, onClose, onAdicionar, pontosProduto) e aceita duas
// opcionais: pontosOriginais (preço sem promoção) e saldoDisponivel.

import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Gift, Minus, Plus, ShoppingCart, Truck, X, Zap } from "lucide-react";
import { estaEmPromocao, exigeVoltagem, formatarPontos, getImagemUrl } from "../utils/tikpontos";
import "./ModalPremioDetalhe.css";

function ModalPremioDetalhe({
  premio,
  aberto,
  onClose,
  onAdicionar,
  pontosProduto = 0,
  pontosOriginais,
  saldoDisponivel,
}) {
  const [quantidade, setQuantidade] = useState(1);
  const fecharRef = useRef(null);

  // Volta para 1 a cada prêmio aberto
  useEffect(() => {
    if (aberto) setQuantidade(1);
  }, [aberto, premio?.id]);

  useEffect(() => {
    if (!aberto) return;
    const anterior = document.activeElement;
    document.body.style.overflow = "hidden";
    fecharRef.current?.focus();
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
      anterior?.focus?.();
    };
  }, [aberto, onClose]);

  if (!aberto || !premio) return null;

  const promo = estaEmPromocao(premio) && pontosOriginais > pontosProduto;
  const total = pontosProduto * quantidade;
  const temSaldo = typeof saldoDisponivel === "number";
  const sobra = temSaldo ? saldoDisponivel - total : 0;
  const voltagens = String(premio.voltagens_disponiveis || "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);

  const mudarQtd = (valor) => setQuantidade(Math.max(1, Math.min(99, Number(valor) || 1)));

  return createPortal(
    <div className="mpd tk-app-portal" onClick={onClose}>
      <div
        className="mpd-caixa"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mpd-titulo"
        onClick={(e) => e.stopPropagation()}
      >
        <button ref={fecharRef} type="button" className="mpd-fechar" aria-label="Fechar" onClick={onClose}>
          <X size={20} />
        </button>

        <div className="mpd-foto">
          {premio.imagem ? (
            <img src={getImagemUrl(premio.imagem)} alt={premio.nome} />
          ) : (
            <Gift size={48} aria-hidden="true" />
          )}
          {promo && <span className="mpd-selo">Promoção</span>}
        </div>

        <div className="mpd-info">
          {premio.categoria && <span className="mpd-categoria">{premio.categoria}</span>}
          <h2 id="mpd-titulo" className="mpd-nome">{premio.nome}</h2>

          <div className="mpd-preco">
            <strong>{formatarPontos(pontosProduto)} pts</strong>
            {promo && <s>{formatarPontos(pontosOriginais)} pts</s>}
          </div>

          {premio.descricao && <p className="mpd-descricao">{premio.descricao}</p>}

          <ul className="mpd-avisos">
            <li>
              <Truck size={17} aria-hidden="true" />
              {premio.frete_gratis ? (
                <span><strong>Frete grátis</strong></span>
              ) : (
                <span>O frete é calculado no carrinho, conforme o seu endereço.</span>
              )}
            </li>
            {exigeVoltagem(premio) && (
              <li>
                <Zap size={17} aria-hidden="true" />
                <span>
                  Disponível em {voltagens.length ? voltagens.join(" e ") : "mais de uma voltagem"}. Você escolhe no carrinho.
                </span>
              </li>
            )}
          </ul>

          <div className="mpd-compra">
            <div className="mpd-qtd">
              <span id="mpd-qtd-rotulo">Quantidade</span>
              <div className="mpd-qtd-controle" role="group" aria-labelledby="mpd-qtd-rotulo">
                <button type="button" aria-label="Diminuir quantidade" onClick={() => mudarQtd(quantidade - 1)} disabled={quantidade <= 1}>
                  <Minus size={16} />
                </button>
                <input
                  type="number"
                  min={1}
                  max={99}
                  value={quantidade}
                  aria-label="Quantidade"
                  onChange={(e) => mudarQtd(e.target.value)}
                />
                <button type="button" aria-label="Aumentar quantidade" onClick={() => mudarQtd(quantidade + 1)}>
                  <Plus size={16} />
                </button>
              </div>
            </div>
            <div className="mpd-total">
              <span>Total do produto</span>
              <strong>{formatarPontos(total)} pts</strong>
            </div>
          </div>

          {temSaldo && (
            <p className={`mpd-saldo ${sobra >= 0 ? "is-ok" : "is-falta"}`}>
              {sobra >= 0
                ? `Sobram ${formatarPontos(sobra)} pts do seu saldo (sem contar o frete).`
                : `Faltam ${formatarPontos(-sobra)} pts para este resgate.`}
            </p>
          )}

          <button
            type="button"
            className="tk-btn tk-btn--primario tk-btn--bloco mpd-adicionar"
            onClick={() => onAdicionar?.(premio, quantidade)}
          >
            <ShoppingCart size={18} aria-hidden="true" />
            Adicionar ao carrinho
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ModalPremioDetalhe;
